/**
 * Test Input Form Component
 * Simplified: Only textarea with Generate button
 */

import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { apiClient } from '../services/api';
import { Send, ChevronRight } from 'lucide-react';

export function TestInputForm() {
  const { state, dispatch } = useApp();
  const [localTestCase, setLocalTestCase] = useState(state.testCase);
  const [loading, setLoading] = useState(false);

  // Sync with state when it changes
  useEffect(() => {
    if (state.status === 'idle') {
      setLocalTestCase(state.testCase);
    }
  }, [state.status, state.testCase]);

  const handleGenerateSteps = async () => {
    if (!localTestCase.trim()) {
      dispatch({ type: 'SET_ERROR', payload: 'Please provide a test case description' });
      return;
    }

    // Extract URL from test case - improved URL detection and validation
    let extractedUrl = '';
    
    // Helper function to clean and validate URL
    const cleanAndValidateUrl = (url: string): string | null => {
      if (!url) return null;
      
      // Remove leading/trailing whitespace
      url = url.trim();
      
      // Remove trailing punctuation (but keep / for paths)
      url = url.replace(/[.,;:!?\)]+$/, '');
      
      // Ensure URL has protocol
      if (!url.match(/^https?:\/\//i)) {
        if (url.startsWith('www.')) {
          url = 'https://' + url;
        } else if (url.match(/^[a-zA-Z0-9-]+\.[a-zA-Z]{2,}/)) {
          url = 'https://' + url;
        } else {
          return null;
        }
      }
      
      // Validate URL format
      try {
        const urlObj = new URL(url);
        // Ensure we have a valid hostname
        if (!urlObj.hostname || urlObj.hostname.length < 3) {
          return null;
        }
        // Return cleaned URL (protocol + hostname + pathname + search)
        return urlObj.protocol + '//' + urlObj.hostname + urlObj.pathname + urlObj.search;
      } catch (e) {
        return null;
      }
    };
    
    // First, try to find URLs with protocol (https:// or http://)
    // Match: protocol://domain.com/path?query - comprehensive URL pattern
    // This pattern matches: https://domain.com/path?query=value#fragment
    // Stops at: space, closing parenthesis, comma, or sentence-ending punctuation
    const urlPattern = /https?:\/\/(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?::[0-9]+)?(?:\/[^\s\)\,\.]*)?(?:\?[^\s\)\,\.]*)?(?:#[^\s\)\,\.]*)?/;
    const urlWithProtocolMatch = localTestCase.match(urlPattern);
    if (urlWithProtocolMatch) {
      let url = urlWithProtocolMatch[0];
      // Remove trailing punctuation that might be part of sentence (but keep /, ?, # for URLs)
      url = url.replace(/[.,;:!?\)]+$/, '');
      const cleaned = cleanAndValidateUrl(url);
      if (cleaned) {
        extractedUrl = cleaned;
      }
    }
    
    // If not found, try www. URLs
    if (!extractedUrl) {
      const wwwUrl = localTestCase.match(/www\.[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s\)\,\.]*)?/);
      if (wwwUrl) {
        const cleaned = cleanAndValidateUrl(wwwUrl[0]);
        if (cleaned) {
          extractedUrl = cleaned;
        }
      }
    }
    
    // If still not found, try domain patterns (e.g., example.com, sahel-testing.com)
    if (!extractedUrl) {
      // Match domain patterns: word.word (at least 2 parts, valid TLD)
      const domainPattern = /[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.[a-zA-Z]{2,}(?:\/[^\s\)\,\.]*)?/g;
      const domains = localTestCase.match(domainPattern);
      if (domains && domains.length > 0) {
        // Filter out common non-URL words and emails
        const validDomains = domains.filter(d => {
          const cleaned = d.replace(/[.,;:!?\)]+$/, '');
          // Skip if it looks like an email
          if (cleaned.includes('@')) return false;
          // Must have at least 2 parts (domain.tld)
          const parts = cleaned.split('.');
          if (parts.length < 2) return false;
          // Last part should be a valid TLD (2+ chars)
          const tld = parts[parts.length - 1].toLowerCase();
          if (tld.length < 2) return false;
          return true;
        });
        
        if (validDomains.length > 0) {
          const cleaned = cleanAndValidateUrl(validDomains[0]);
          if (cleaned) {
            extractedUrl = cleaned;
          }
        }
      }
    }
    
    // If no URL found, try to use the stored URL
    const websiteUrl = extractedUrl || state.websiteUrl || '';

    if (!websiteUrl) {
      dispatch({ type: 'SET_ERROR', payload: 'Please include a website URL in your test case description (e.g., https://example.com)' });
      return;
    }
    
    // Final validation - ensure URL is valid before sending
    try {
      new URL(websiteUrl);
      console.log('[TestInputForm] Extracted URL:', websiteUrl);
    } catch (e) {
      console.error('[TestInputForm] Invalid URL:', websiteUrl, e);
      dispatch({ type: 'SET_ERROR', payload: `Invalid URL format: ${websiteUrl}. Please use a valid URL like https://example.com` });
      return;
    }

    setLoading(true);
    dispatch({ type: 'SET_STATUS', payload: 'generating_steps' });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    console.log('[TestInputForm] Sending request with:', {
      test_case: localTestCase,
      website_url: websiteUrl,
      assertions: state.assertions
    });

    try {
      const response = await apiClient.generateSteps({
        test_case: localTestCase,
        website_url: websiteUrl,
        assertions: state.assertions,
      });

      dispatch({ type: 'SET_SESSION_ID', payload: response.session_id });
      dispatch({ type: 'SET_TEST_CASE', payload: localTestCase });
      dispatch({ type: 'SET_WEBSITE_URL', payload: websiteUrl });
      dispatch({ type: 'SET_STEPS', payload: response.steps });
      dispatch({ type: 'SET_STATUS', payload: 'steps_ready' });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to generate steps' });
      dispatch({ type: 'SET_STATUS', payload: 'idle' });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleGenerateSteps();
    }
  };

  return (
    <div className="w-full">
      <div className="relative">
        <textarea
          value={localTestCase}
          onChange={(e) => setLocalTestCase(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Go to https://example-testing.com and verify the search bar, Most used service and the types are displayed"
          disabled={loading || state.status !== 'idle'}
          className="testCaseInput w-full bg-gray-100 border border-gray-200 rounded-lg px-4 py-3 pr-12 resize-none focus:outline-none   disabled:opacity-50 disabled:cursor-not-allowed"
          rows={4}
        />
        <button
          type="button"
          onClick={handleGenerateSteps}
          disabled={loading || state.status !== 'idle' || !localTestCase.trim()}
          className="testCaseGenerateButton absolute right-3 bottom-3 w-10 h-10  text-white rounded-full flex items-center justify-center hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Generate Steps (Ctrl+Enter)"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
      
      {state.error && (
        <div className="error-message mt-2">
          ❌ {state.error}
        </div>
      )}
    </div>
  );
}
